// src/components/imagery/TimeStrip/ReviewTimeStripMap.jsx
//
// The time strip's Review adapter map: one point, one epoch at a time, and a
// swipe to a compare epoch. ReviewMap's rules hold here, for ReviewMap's
// reasons (wellfinder/ReviewMap.jsx's header): a panel inside a workflow, no
// layer stack, a FIXED zoom (REVIEW_ZOOM) that every item reopens at, manual
// zoom allowed and reset when the item changes.
//
// THE SWIPE. Two maps, stacked: the base shows the cursor's epoch, the overlay
// the compare epoch, and the overlay is clipped to the left of the divider. The
// overlay follows the base's camera and takes no input of its own. MapLibre
// cannot clip a layer, so two maps is the honest way to show two rasters side
// by side at one place.
//
// THE DIVIDER is a line the map's full height with a grab handle, dragged by
// either. Its position is a percentage of the map container, and so are the
// line's left and the overlay's clip (the overlay is inset-0 in the same
// container), so the split is where the line is at any size and after any
// resize. The pointer is measured against the container's box at each move.
// It replaced a range input half the map's width, centred, whose 0-100 drove a
// line and clip measured on the whole map: the line ran at twice the pointer's
// speed and left it behind.
//
// THE ONLY TILE PATH, as in CellMap: a key from GET /tile/session through the
// token cache, then /tiles/WebMercatorQuad with dataset and ck. A null key is a
// real state and shows the Mapbox basemap, not blank tiles.
//
// A CORRECTED POSITION (DB 2026-10-25). Given a draft, the base map carries a
// draggable marker at it, reports each drag (onDraftMove), and draws a line
// from the item's point (the hollow ring, which stays where it is) to the
// draft with the distance beside its middle, amber when the page says (past
// 10 m, DB 2026-10-26). The marker
// and the distance are marker elements: MapLibre's .maplibregl-marker class
// positions them, so their classes are added once and their state toggled,
// never assigned (assigning className dropped that class: the label laid out
// as a full-width block, the marker stayed in place only while it was the
// container's first child). The compare swipe
// is hidden while drafting: the position is placed on the cursor's epoch, the
// one the base map shows, so nothing else may cover it.

import { useCallback, useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import { whenMapLoaded } from "../../../lib/map/whenMapLoaded";
import { getOrFetchTileToken, bindTileAuthHandler } from "../../../api/tileTokens";
import { buildTransformRequest } from "../../../api/mapTransformRequest";
import { tileUrl } from "../../../api/tileUrl";
import { attachAttribution, basemapAttribution, SATELLITE_STYLE_ID } from "../../../lib/map/attribution";
import { useAuthStore } from "../../../stores/useAuthStore";
import { getConfig } from "../../../config";
import { REVIEW_ZOOM } from "../../wellfinder/reviewMapEncoding";

const RASTER_SRC = "ts-imagery";
const RASTER_LAYER = "ts-imagery-raster";
const POINT_SRC = "ts-point";
const POINT_LAYER = "ts-point-circle";
export const DRAFT_LINE_SRC = "ts-draft-line";
export const DRAFT_LINE_LAYER = "ts-draft-line";
const DRAFT_COLOR = "#ffffff";
const DRAFT_AMBER_COLOR = "#f59e0b";
const EMPTY = { type: "FeatureCollection", features: [] };

/**
 * Show this URL's raster, or none. Add, retarget or remove: CellMap's
 * applyImagerySource plus the removal a strip needs, since moving the cursor to
 * an epoch with no key must not leave the previous epoch's tiles on screen.
 *
 * @returns {"added"|"retargeted"|"unchanged"|"removed"|"absent"}
 */
export function setRaster(map, url, beforeLayerId) {
  const existing = map.getSource(RASTER_SRC);
  if (!url) {
    if (!existing) return "absent";
    if (map.getLayer(RASTER_LAYER)) map.removeLayer(RASTER_LAYER);
    map.removeSource(RASTER_SRC);
    return "removed";
  }
  if (!existing) {
    map.addSource(RASTER_SRC, { type: "raster", tiles: [url], tileSize: 256, maxzoom: 20 });
    map.addLayer({ id: RASTER_LAYER, type: "raster", source: RASTER_SRC }, beforeLayerId);
    return "added";
  }
  if (existing.tiles?.[0] !== url) {
    existing.setTiles([url]);
    return "retargeted";
  }
  return "unchanged";
}

const pointPaint = {
  "circle-radius": 7,
  "circle-color": "rgba(0,0,0,0)",
  "circle-stroke-color": "#ff7a1a",
  "circle-stroke-width": 2.5,
};

function makeMap(container, { interactive }) {
  const map = new maplibregl.Map({
    container,
    style: {
      version: 8,
      sources: {
        "mapbox-satellite": {
          type: "raster",
          tiles: [`https://api.mapbox.com/v4/mapbox.satellite/{z}/{x}/{y}@2x.jpg90?access_token=${getConfig().mapboxToken}`],
          tileSize: 256,
          attribution: basemapAttribution(SATELLITE_STYLE_ID),
        },
      },
      layers: [{ id: "mapbox-satellite", type: "raster", source: "mapbox-satellite" }],
    },
    center: [-102, 32],
    zoom: REVIEW_ZOOM,
    maxzoom: 20,
    interactive,
    attributionControl: { compact: false },
    transformRequest: buildTransformRequest(() => useAuthStore.getState().token),
  });
  // Mapbox's logo and credit (REM-684), on each of the swipe's maps.
  attachAttribution(map, { mapboxSource: "mapbox-satellite" });
  bindTileAuthHandler(map);
  whenMapLoaded(map, () => {
    // The draft's line under the ring, the raster under both (useRaster).
    map.addSource(DRAFT_LINE_SRC, { type: "geojson", data: EMPTY });
    map.addLayer({ id: DRAFT_LINE_LAYER, type: "line", source: DRAFT_LINE_SRC,
      paint: { "line-color": DRAFT_COLOR, "line-width": 2, "line-dasharray": [2, 1] } });
    map.addSource(POINT_SRC, { type: "geojson", data: EMPTY });
    map.addLayer({ id: POINT_LAYER, type: "circle", source: POINT_SRC, paint: pointPaint });
  });
  return map;
}

/** Keeps one map's raster on one dataset key. */
function useRaster(mapRef, readyRef, datasetKey) {
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;
    let cancelled = false;
    const run = (url) => {
      const apply = () => setRaster(map, url, DRAFT_LINE_LAYER);
      if (readyRef.current) apply(); else map.once("load", apply);
    };
    if (!datasetKey) { run(null); return undefined; }
    (async () => {
      try {
        const { ck } = await getOrFetchTileToken(datasetKey);
        if (!cancelled && mapRef.current) run(tileUrl(datasetKey, ck));
      } catch {
        if (!cancelled && mapRef.current) run(null);   // the basemap stands in
      }
    })();
    return () => { cancelled = true; };
  }, [mapRef, readyRef, datasetKey]);
}

/**
 * @param {object} props
 * @param {{type: "Point", coordinates: number[]}|null} props.point
 * @param {string|null} props.datasetKey   the cursor's epoch
 * @param {string|null} [props.compareKey] the compare epoch; null hides the swipe
 * @param {string} [props.label]           the cursor's label, drawn on the base side
 * @param {string} [props.compareLabel]
 * @param {number[]|null} [props.draft]     a corrected position being placed, [lon, lat]
 * @param {string} [props.distanceLabel]    the draft's distance from the point, as shown
 * @param {boolean} [props.amber]           drawn amber (the page's warning, past 10 m)
 * @param {(lonLat: number[]) => void} [props.onDraftMove]
 */
export default function ReviewTimeStripMap({
  point, datasetKey, compareKey = null, label = "", compareLabel = "",
  draft = null, distanceLabel = "", amber = false, onDraftMove,
}) {
  const baseHolder = useRef(null);
  const overHolder = useRef(null);
  const baseRef = useRef(null);
  const overRef = useRef(null);
  const baseReady = useRef(false);
  const overReady = useRef(false);
  const rootRef = useRef(null);
  const [swipe, setSwipe] = useState(50);
  const swipeEnd = useRef(null);

  // Drag the divider: the pointer's x across the container, as a percentage,
  // held to the map. Window listeners, so a drag that leaves the line keeps it.
  const startSwipe = useCallback((e) => {
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    const to = (clientX) => {
      const r = rootRef.current?.getBoundingClientRect();
      if (!r || !r.width) return;
      setSwipe(Math.min(100, Math.max(0, ((clientX - r.left) / r.width) * 100)));
    };
    const move = (ev) => to(ev.clientX);
    const end = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
      swipeEnd.current = null;
    };
    swipeEnd.current?.();
    swipeEnd.current = end;
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
  }, []);
  useEffect(() => () => swipeEnd.current?.(), []);
  const draftMarks = useRef({ marker: null, chip: null });
  const onDraftMoveRef = useRef(onDraftMove);
  onDraftMoveRef.current = onDraftMove;

  useEffect(() => {
    if (!baseHolder.current || !overHolder.current || baseRef.current) return undefined;
    const base = makeMap(baseHolder.current, { interactive: true });
    const over = makeMap(overHolder.current, { interactive: false });
    baseRef.current = base; overRef.current = over;
    window.__mapRecheck = base;
    whenMapLoaded(base, () => { baseReady.current = true; });
    whenMapLoaded(over, () => { overReady.current = true; });
    const follow = () => over.jumpTo({ center: base.getCenter(), zoom: base.getZoom(), bearing: base.getBearing(), pitch: base.getPitch() });
    base.on("move", follow);
    return () => {
      draftMarks.current.marker?.remove(); draftMarks.current.chip?.remove();
      draftMarks.current = { marker: null, chip: null };
      for (const m of [base, over]) { try { m.remove(); } catch { /* already gone */ } }
      baseRef.current = null; overRef.current = null; baseReady.current = false; overReady.current = false;
    };
  }, []);

  useRaster(baseRef, baseReady, datasetKey);
  useRaster(overRef, overReady, compareKey);

  // A new item reopens at the fixed zoom on its point, whatever the reviewer
  // zoomed to on the last one.
  const pointKey = point ? point.coordinates.join(",") : "";
  useEffect(() => {
    const data = point
      ? { type: "FeatureCollection", features: [{ type: "Feature", geometry: point, properties: {} }] }
      : { type: "FeatureCollection", features: [] };
    for (const [ref, ready] of [[baseRef, baseReady], [overRef, overReady]]) {
      const map = ref.current;
      if (!map) continue;
      const paint = () => {
        map.getSource(POINT_SRC)?.setData(data);
        if (point) map.jumpTo({ center: point.coordinates, zoom: REVIEW_ZOOM });
      };
      if (ready.current) paint(); else map.once("load", paint);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointKey]);

  // The draft: a marker to drag, a line from the point, its distance.
  const draftKey = draft ? draft.join(",") : "";
  useEffect(() => {
    const map = baseRef.current;
    if (!map) return;
    const apply = () => {
      const d = draftMarks.current;
      const line = map.getSource(DRAFT_LINE_SRC);
      if (!draft || !point) {
        d.marker?.remove(); d.chip?.remove();
        d.marker = null; d.chip = null;
        line?.setData(EMPTY);
        return;
      }
      if (!d.marker) {
        const el = document.createElement("div");
        el.setAttribute("data-testid", "recheck-draft-marker");
        el.classList.add("h-4", "w-4", "cursor-move", "rounded-full", "border-2", "border-white", "shadow");
        d.marker = new maplibregl.Marker({ element: el, draggable: true }).setLngLat(draft).addTo(map);
        d.marker.on("drag", () => {
          const ll = d.marker.getLngLat();
          onDraftMoveRef.current?.([ll.lng, ll.lat]);
        });
        const chip = document.createElement("div");
        chip.setAttribute("data-testid", "recheck-draft-distance");
        chip.classList.add("pointer-events-none", "w-max", "whitespace-nowrap", "rounded", "px-1.5", "py-0.5",
          "bg-black/75", "text-[11px]", "font-medium", "shadow");
        // Its left edge 8 px right of the line's middle: beside the line, not on it.
        d.chip = new maplibregl.Marker({ element: chip, anchor: "left", offset: [8, 0] }).setLngLat(draft).addTo(map);
      }
      d.marker.setLngLat(draft);
      d.marker.getElement().classList.toggle("bg-amber-500", amber);
      d.marker.getElement().classList.toggle("bg-sky-400", !amber);
      const [x0, y0] = point.coordinates;
      d.chip.setLngLat([(x0 + draft[0]) / 2, (y0 + draft[1]) / 2]);
      const chip = d.chip.getElement();
      chip.textContent = distanceLabel;
      chip.setAttribute("data-amber", String(amber));
      chip.classList.toggle("text-amber-300", amber);
      chip.classList.toggle("text-white", !amber);
      line?.setData({ type: "FeatureCollection", features: [
        { type: "Feature", geometry: { type: "LineString", coordinates: [point.coordinates, draft] }, properties: {} }] });
      if (map.getLayer(DRAFT_LINE_LAYER)) map.setPaintProperty(DRAFT_LINE_LAYER, "line-color", amber ? DRAFT_AMBER_COLOR : DRAFT_COLOR);
    };
    if (baseReady.current) apply(); else map.once("load", apply);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey, pointKey, amber, distanceLabel]);

  const comparing = Boolean(compareKey) && !draft;
  return (
    <div ref={rootRef} className="relative h-full w-full overflow-hidden" data-testid="recheck-map">
      <div ref={baseHolder} className="absolute inset-0" />
      <div
        ref={overHolder}
        className="absolute inset-0 pointer-events-none"
        style={{ clipPath: comparing ? `inset(0 ${100 - swipe}% 0 0)` : "inset(0 100% 0 0)" }}
        data-testid="recheck-map-compare"
      />
      {comparing && (
        <>
          <div
            role="slider" aria-orientation="horizontal"
            aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(swipe)}
            aria-label="Divider between the compare epoch (left) and the cursor's epoch (right)"
            data-testid="recheck-swipe"
            onPointerDown={startSwipe}
            className="absolute top-0 bottom-0 z-10 w-4 -translate-x-1/2 cursor-ew-resize touch-none select-none"
            style={{ left: `${swipe}%` }}
          >
            <div data-testid="recheck-swipe-line" className="mx-auto h-full w-0.5 bg-white/90 shadow" />
            <div
              data-testid="recheck-swipe-thumb"
              className="absolute top-1/2 left-1/2 h-8 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white bg-black/60 shadow"
            />
          </div>
          <div className="absolute top-2 left-2 rounded bg-bg/80 px-1.5 py-0.5 text-[11px] text-fg-primary pointer-events-none">{compareLabel}</div>
          <div className="absolute top-2 right-2 rounded bg-bg/80 px-1.5 py-0.5 text-[11px] text-fg-primary pointer-events-none">{label}</div>
        </>
      )}
    </div>
  );
}
